import React, {useCallback, useEffect, useMemo, useState} from "react";
import styled from "styled-components";
import {reportsStatics, reportsStaticsAll, reportsStaticsAllColumn, reportsUserStaticsAllColumn} from "./entity/period";
import {BoardSearchResult, ChartTooltip} from "../../assets/GlobalStyles";
import {atom, useAtom, useAtomValue, useSetAtom} from "jotai";
import ReportTable from "../../components/table/ReportTable";
import HorizontalScrollGuide from "../../components/table/HorizontalScrollGuide";
import {ReportsCondition} from "../../components/reports/Condition";
import {ResponsiveBar} from "@nivo/bar";
import {sort} from "../../components/reports/sortList";
import {dateFormat, decimalFormat, moneyToFixedFormat, numberToFixedFormat} from "../../common/StringUtils";
import {lockedRows, summaryReducer} from "./entity/common";
import {adminInfo, tokenResultAtom} from "../login/entity";
import {selectAdminStaticsAll, selectUserStaticsAll} from "../../services/reports/periodAxios";
import {UserInfo} from "../layout";
import {calculateEcpm, formatEcpm, metricColor} from "./entity/metrics";
import useInventoryOptions from "./hooks/useInventoryOptions";
import ReportMetricPanel from "../../components/reports/ReportMetricPanel";

const formatRevenue = value => Number(value || 0).toLocaleString("ko-KR", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/** 일자별 차트 **/
function MyResponsiveBar(props) {
  const {selectKeys, data} = props
  const [periodData,setPeriodData] = useAtom(reportsStaticsAll)

  useEffect(() => {
    setPeriodData(data);
    setIsAnimationActive(true);

    const timer = setTimeout(() => {
      setIsAnimationActive(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, [data, setPeriodData]);

  const yFormatted = (itemData) => {
    let value;
    if(['clickRate'].includes(itemData.id)) {
      value = numberToFixedFormat(itemData.value)+'%'
    } else if(itemData.id === 'revenueAmount') {
      value = formatRevenue(itemData.value)+'원'
    } else if(itemData.id === 'ecpm') {
      value = formatEcpm(itemData.value)+'원'
    } else if(['costAmount', 'cpc'].includes(itemData.id)) {
      value = moneyToFixedFormat(itemData.value)+'원'
    } else {
      value = decimalFormat(itemData.value)
    }
    return value
  }

  const dateFormatted = (tick) => {
    let value;
    if (periodData.length > 31) {
      value = tick.tickIndex%5 === 0 ? tick.value : null
    } else {
      value = tick.value
    }
    return dateFormat(value, 'MM.DD')
  }
  const [isAnimationActive, setIsAnimationActive] = useState(false);

  const tickComponent = (tick) => {
    return (
      <g
        transform={`translate(${tick.x},${tick.y})`}
        style={{
          opacity: isAnimationActive ? 0 : 1,
          transition: isAnimationActive ? 'opacity 0.5s ease-in' : 'opacity 0.5s ease-out',
        }}
      >
        <line />
        <text
          textAnchor="middle"
          dominantBaseline="middle"
          transform={`translate(${tick.textX}, ${tick.textY})`}
          fontSize={12}
        >
          {dateFormatted(tick)}
        </text>
      </g>
    );
  };

  return (
    <ResponsiveBar
      data={periodData.length !== 0 ? periodData : []}
      keys={selectKeys}
      indexBy="historyDate"
      groupMode="grouped"
      margin={{top: 30, right: 30, bottom: 80, left: 64}}
      padding={0.5}
      height={340}
      valueScale={{type: 'linear', min: 0, max: 'auto', nice: true}}
      indexScale={{type: 'band', round: true}}
      colors={({id})=>metricColor(id)}
      axisLeft={{
        tickSize: 0,
        tickPadding: 10,
        tickRotation: 0,
        tickValues: 5,
        format: decimalFormat
      }}
      animate={true}
      motionStiffness={120}
      motionDamping={15}
      axisBottom={{
        tickSize: 0,
        tickPadding: 15,
        legendOffset: 32,
        renderTick: tickComponent
      }}
      tooltip={(props) => {
        return (
          <ChartTooltip>
            <p>{props.indexValue}</p>
            <p>{yFormatted(props)}</p>
          </ChartTooltip>
        )
      }}
      enableLabel={false}
      enableGridY={true}
      layers={[
        'grid',
        'axes',
        'bars',
        'markers',
        'legends',
      ]}
    />
  )
}

export const AdminInfo = atom(adminInfo)
/** 일자별 보고서 **/
export default function ReportsPeriod(){
  const [searchCondition, setSearchCondition] = useState(reportsStatics);
  const [searchState, setSearchState] = useState(reportsStatics);
  const [chartKeys, setChartKeys] = useState(['requestCount', 'exposureCount', 'revenueAmount', 'ecpm']);
  const [totalCount, setTotalCount] = useState(0);
  const [chartPageSize, setChartPageSize] = useState(30);
  const setPeriodData = useSetAtom(reportsStaticsAll);
  const tokenInfoState = useAtomValue(tokenResultAtom);
  const [creativeInfo, setCreativeInfo] = useState({});
  const userInfoState = useAtomValue(UserInfo);
  const [chartData, setChartData] = useState([]);
  const [isConditionOpen, setIsConditionOpen] = useState(false);
  const [downloadTrigger, setDownloadTrigger] = useState(0);
  const {inventoryOptions,loadInventoryOptions,clearInventoryOptions}=useInventoryOptions({searchState,tokenInfoState,requireMedia:true});

  const chartSummary = useMemo(() => chartData.reduce((summary, item) => ({
    requestCount: summary.requestCount + (Number(item.requestCount) || 0),
    exposureCount: summary.exposureCount + (Number(item.exposureCount) || 0),
    revenueAmount: summary.revenueAmount + (Number(item.revenueAmount) || 0),
  }), {requestCount: 0, exposureCount: 0, revenueAmount: 0}), [chartData]);

  const handleSearchCondition = useCallback(
      async ({skip, limit, sortInfo}) => {
        const condition = {
          ...searchCondition,
          pageSize: 30,
          currentPage: skip / limit === 0 ? 1 : skip / limit + 1,
          sortType: sort('DATE_DESC', sortInfo),
        };

        if (tokenInfoState.role === 'NORMAL') {
          const response = await selectUserStaticsAll(tokenInfoState.id, condition);

          if (response !== null) {
            const data = response.content;
            setTotalCount(response.totalElements);

            return {
              data,
              count: response.totalElements,
            };
          }

          return {
            data: [],
            count: 0,
          };
        }

        const response = await selectAdminStaticsAll(creativeInfo.id, condition);

        if (response !== null) {
          const data = response.content;
          setTotalCount(response.totalElements);

          return {
            data,
            count: response.totalElements,
          };
        }

        return {
          data: [],
          count: 0,
        };
      },
      [
        searchCondition,
        creativeInfo.id,
        tokenInfoState.id,
        tokenInfoState.role,
      ]
  );

  const handleChartSearchCondition = useCallback(
      async () => {
        const condition = {
          ...searchCondition,
          pageSize: chartPageSize,
          currentPage: 1,
          sortType: 'DATE_ASC',
        };

        if (tokenInfoState.role === 'NORMAL') {
          const response = await selectUserStaticsAll(tokenInfoState.id, condition);

          if (response !== null) {
            const data = response.content.map((item) => ({
              ...item,
              clickRate: item.exposureCount !== 0
                  ? (item.validClickCount / item.exposureCount) * 100
                  : 0,
              cpc: item.validClickCount !== 0
                  ? item?.costAmount / item.validClickCount
                  : 0,
              ecpm: calculateEcpm(item.revenueAmount, item.exposureCount),
            }));

            setPeriodData(data);
            setChartData(data);
          }

          return;
        }

        const response = await selectAdminStaticsAll(creativeInfo.id, condition);

        if (response !== null) {
          const data = response.content.map((item) => ({
            ...item,
            clickRate: item.exposureCount !== 0
                ? (item.validClickCount / item.exposureCount) * 100
                : 0,
            cpc: item.validClickCount !== 0
                ? item?.costAmount / item.validClickCount
                : 0,
            ecpm: calculateEcpm(item.revenueAmount, item.exposureCount),
          }));

          setPeriodData(data);
          setChartData(data);
        }
      },
      [
        searchCondition,
        chartPageSize,
        creativeInfo.id,
        tokenInfoState.id,
        tokenInfoState.role,
        setPeriodData,
      ]
  );

  useEffect(() => {
    handleChartSearchCondition();
  }, [handleChartSearchCondition]);
  /**
   * 차트 키값 선택
   * @param key
   */
  const handleChangeChartKey = (keys) => setChartKeys(keys)

  const onSearch = ({mediaInfo, chartPageSize: nextChartPageSize} = {}) => {
    setCreativeInfo(mediaInfo || {})
    if (nextChartPageSize) {
      setChartPageSize(nextChartPageSize)
    }
    setSearchCondition({
      ...searchState
    })
  }

  const handleSearchAdvertiser = (data) => {
    setCreativeInfo(data)
  }

  const handleClickReset = () => {
    setCreativeInfo({})
    clearInventoryOptions()
    setSearchState(current => ({...current, inventoryId: null}))
  }

  const selectedInventoryNames = useMemo(() => {
    const selectedIds = searchCondition.inventoryId
        ? String(searchCondition.inventoryId).split(",").filter(Boolean)
        : [];
    if (!selectedIds.length) return "전체";
    const names = inventoryOptions
        .filter(option => selectedIds.includes(String(option.value)))
        .map(option => option.label);
    return names.length === 1 ? names[0] : `${names.length || selectedIds.length}개 지면`;
  }, [inventoryOptions, searchCondition.inventoryId]);

  return(
      <PeriodReportPage>
        <ReportCard $condition>
          <SectionHeader
              $clickable
              $open={isConditionOpen}
              role="button"
              tabIndex={0}
              aria-expanded={isConditionOpen}
              onClick={() => setIsConditionOpen(open => !open)}
              onKeyDown={event => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setIsConditionOpen(open => !open);
                }
              }}
          >
            <SectionHeading>
              <SectionAccent/>
              <div>
                <strong>조회 조건</strong>
                <span>매체, 광고 상품, 디바이스와 기간을 기준으로 일자별 데이터를 조회합니다.</span>
              </div>
            </SectionHeading>
            <ConditionHeaderRight>
              {!isConditionOpen && (
                  <ConditionSummary>
                    <SummaryChip>매체 <b>{creativeInfo.siteName || "전체"}</b></SummaryChip>
                    <SummaryChip>지면 <b>{selectedInventoryNames}</b></SummaryChip>
                    <SummaryChip>상품 <b>{searchCondition.productType || "전체"}</b></SummaryChip>
                    <SummaryChip>디바이스 <b>{searchCondition.deviceType || "전체"}</b></SummaryChip>
                    <SummaryChip>기간 <b>{searchCondition.searchStartDate} ~ {searchCondition.searchEndDate}</b></SummaryChip>
                  </ConditionSummary>
              )}
              <CollapseIcon $open={isConditionOpen} aria-hidden="true"/>
            </ConditionHeaderRight>
          </SectionHeader>
          {isConditionOpen && (
              <ConditionBody>
                <ReportsCondition modern searchMediaInfo={creativeInfo} searchMedia={handleSearchAdvertiser} searchMediaReset={handleClickReset} searchState={searchState} setSearchState={setSearchState} setChartPageSize={setChartPageSize} onSearch={onSearch}
                    showInventory={tokenInfoState.role !== "NORMAL"} inventoryMulti inventoryOptions={inventoryOptions}
                    inventoryRequiresMedia
                    onMediaDraftChange={loadInventoryOptions}/>
              </ConditionBody>
          )}
        </ReportCard>

        <ReportCard>
          <ReportMetricPanel summary={chartSummary} chartKeys={chartKeys} onChartKeyChange={handleChangeChartKey}
              description="확인할 지표를 여러 개 선택하면 차트에 함께 반영됩니다." showEcpm>
            <MyResponsiveBar selectKeys={chartKeys} data={chartData}/>
          </ReportMetricPanel>
        </ReportCard>

        <ReportCard>
          <SectionHeader>
            <SectionHeading>
              <SectionAccent/>
              <div>
                <strong>일자별 데이터</strong>
                <span>조회 조건에 해당하는 일자별 상세 성과 데이터를 확인합니다.</span>
              </div>
            </SectionHeading>
            <ExcelHeaderButton type="button" onClick={() => setDownloadTrigger(trigger => trigger + 1)}>
              엑셀 저장
            </ExcelHeaderButton>
          </SectionHeader>
          <TableBody>
            <HorizontalScrollGuide/>
            <BoardSearchResult>
              <ReportTable columns={userInfoState.email === '' ? reportsStaticsAllColumn : reportsUserStaticsAllColumn}
                     lockedRows={lockedRows}
                     summaryReducer={summaryReducer}
                     totalCount={[totalCount,'보고서']}
                     data={handleSearchCondition}
                     defaultSortInfo={{name:"historyDate", dir: -1}}
                     pagination
                     livePagination
                     downloadList={false}
                     downloadTrigger={downloadTrigger}
                     fileName={'일자별_보고서'}
                     scrollThreshold={0.7}
                     headerHeight={44}
                     style={{minHeight: 500}}/>
            </BoardSearchResult>
          </TableBody>
        </ReportCard>
      </PeriodReportPage>
  )
}
/** 스티일 시트 **/
const PeriodReportPage = styled.div`
  display:flex;width:100%;flex-direction:column;gap:16px;
`;
const ReportCard = styled.section`
  position:relative;z-index:${({$condition})=>$condition?10:1};
  overflow:${({$condition})=>$condition?'visible':'hidden'};
  border:1px solid #e1e6eb;border-radius:10px;background:var(--color-surface);
  box-shadow:0 5px 18px rgba(31,41,55,.045);
`;
const SectionHeader = styled.div`
  display:flex;min-height:68px;align-items:center;justify-content:space-between;gap:18px;
  padding:12px 16px;border-bottom:1px solid #e9edf1;background:#fbfcfe;
  border-radius:${({$clickable,$open})=>$clickable?($open?'10px 10px 0 0':'10px'):'10px 10px 0 0'};
  cursor:${({$clickable})=>$clickable?'pointer':'default'};
  transition:background .15s ease;
  ${({$clickable})=>$clickable&&'&:hover{background:#f7faff;}'}
  &:focus-visible{outline:2px solid var(--color-primary-focus);outline-offset:-2px;}
  @media(max-width:900px){align-items:flex-start;flex-direction:column;}
`;
const SectionHeading = styled.div`
  display:flex;min-width:0;align-items:center;gap:10px;
  strong,span{display:block;}strong{color:var(--color-text-strong);font-size: var(--font-size-15);font-weight:750;}
  span{margin-top:4px;color:var(--color-text-muted);font-size: var(--font-size-12);}
`;
const SectionAccent = styled.i`
  width:3px;height:32px;flex:0 0 3px;border-radius:3px;background:var(--color-primary);
`;
const ConditionHeaderRight = styled.div`
  display:flex;min-width:0;align-items:center;justify-content:flex-end;gap:8px;
  @media(max-width:900px){width:100%;justify-content:space-between;}
`;
const ConditionSummary = styled.div`
  display:flex;min-width:0;align-items:center;justify-content:flex-end;gap:6px;flex-wrap:wrap;
`;
const SummaryChip = styled.span`
  display:inline-flex;height:28px;align-items:center;gap:5px;padding:0 9px;
  border:1px solid #dbe7f5;border-radius:999px;background:var(--color-surface-muted);color:#84909c;font-size: var(--font-size-11);white-space:nowrap;
  b{max-width:180px;overflow:hidden;color:var(--color-primary-strong);font-size: var(--font-size-11);text-overflow:ellipsis;}
`;
const CollapseIcon = styled.span`
  display:inline-flex;width:18px;height:28px;flex:0 0 18px;align-items:center;justify-content:center;
  transform:rotate(${({$open})=>$open?'180deg':'0deg'});
  transition:transform .18s ease;

  &::before{
    width:0;
    height:0;
    border-top:5px solid var(--color-primary);
    border-right:4px solid transparent;
    border-left:4px solid transparent;
    content:'';
  }
`;
const ConditionBody = styled.div`
  padding:4px 0 0;border-radius:0 0 10px 10px;background:var(--color-surface);
  >div{margin:0;padding:16px 18px 18px;background:var(--color-surface);}
  [class*="-control"]{min-height:38px;border-color:var(--color-border-strong);border-radius:7px;box-shadow:none;}
  input{font-size: var(--font-size-13);}
`;
const TableBody = styled.div`
  padding:10px 12px 14px;
  ${BoardSearchResult}{margin:0;padding:0;background:var(--color-surface);}
  .InovuaReactDataGrid--theme-default-light{overflow:hidden;border:1px solid var(--color-border-strong);border-radius:8px;font-size: var(--font-size-13);}
  .InovuaReactDataGrid__header,.InovuaReactDataGrid__column-header{border-color:#cddff5 !important;background:#f2f7fd;}
  .InovuaReactDataGrid__header{border-bottom:2px solid #8bb8ea !important;}
`;
const ExcelHeaderButton = styled.button`
  display:inline-flex;min-width:88px;height:34px;align-items:center;justify-content:center;
  padding:0 12px;border:1px solid #d7e5d9;border-radius:6px;background:#f7fbf7;
  color:#47774d;font-size: var(--font-size-12);font-weight:700;cursor:pointer;
  &:hover{border-color:#bcd6c0;background:#eef7ef;}
`;
